import { useCallback, useEffect, useRef } from 'react';

import { requestSdkClose } from 'utils/sdkClose';

interface SdkMessage {
  type: 'RESIZE' | 'AUTH_SUCCESS';
  payload?: any;
}

export function useLeasingJourneySDK() {
  const containerRef = useRef<HTMLDivElement>(null);

  const isEmbedded = typeof window !== 'undefined' && window !== window.parent;

  const sendMessage = useCallback(
    (message: SdkMessage) => {
      if (!isEmbedded) return;

      const params = new URLSearchParams(window.location.search);
      const targetOrigin = params.get('parentOrigin') || '*';
      window.parent.postMessage(message, targetOrigin);
    },
    [isEmbedded],
  );

  useEffect(() => {
    if (!isEmbedded || !containerRef.current) {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        sendMessage({
          type: 'RESIZE',
          payload: { height: entries[0].contentRect.height },
        });
      }
    });

    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, [sendMessage, isEmbedded]);

  const sendAuthSuccess = useCallback(
    (userMetadata: { name: string; email: string }) => {
      sendMessage({ type: 'RESIZE', payload: { height: '100%', fullScreen: true } });
      sendMessage({ type: 'AUTH_SUCCESS', payload: userMetadata });
    },
    [sendMessage],
  );

  const closeModal = useCallback(() => {
    if (isEmbedded) requestSdkClose();
  }, [isEmbedded]);

  return {
    containerRef,
    isEmbedded,
    sendAuthSuccess,
    closeModal,
  };
}
